import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../AddProduct.module.css';
import AxiosUrl from '../../AxiosUrl';

function CategorySection({
    selectCategory,
    categorydata,
    setCategoryData,
    category,
    setSubCategoryData,
    SubCategoryData,
    SubCategoryId,
    setSubCategoryId,
    errorFields,
    setErrorFields,
    handleFieldChange,
    selectedCategories,
    setSelectedCategories,
    brandNames,
    setBrandNames,
}) {
    const uid = localStorage.getItem('id');
    const [categoryTree, setCategoryTree] = useState([]);
    const [brandInput, setBrandInput] = useState('');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const brandRef = useRef(null);

    // Brand pagination state
    const [brandList, setBrandList] = useState([]);
    const [brandPage, setBrandPage] = useState(1);
    const [brandHasMore, setBrandHasMore] = useState(true);
    const [brandLoading, setBrandLoading] = useState(false);
    const [brandSearch, setBrandSearch] = useState('');
    const brandDropdownRef = useRef(null);
    const BRAND_LIMIT = 10;

    // Fetch full category tree
    useEffect(() => {
        AxiosUrl.get(`/category-tree/${uid}`)
            .then((res) => {
                if (res.data.success) {
                    setCategoryTree(res.data.data || []);
                }
            })
            .catch(() => {});
    }, [uid]);

    // Fetch brands with pagination
    const fetchBrands = useCallback(async (page, search, append) => {
        if (brandLoading) return;
        setBrandLoading(true);
        try {
            const endpoint = search.trim()
                ? `/search-brands/${uid}?search=${encodeURIComponent(search.trim())}&page=${page}&limit=${BRAND_LIMIT}`
                : `/brand/${uid}?page=${page}&limit=${BRAND_LIMIT}`;
            const res = await AxiosUrl.get(endpoint);
            const brands = res.data?.data || res.data || [];
            const names = Array.isArray(brands)
                ? brands.map((b) => b.name || b.brandName || b).filter(Boolean)
                : [];
            if (append) {
                setBrandList((prev) => [...prev, ...names]);
            } else {
                setBrandList(names);
            }
            setBrandHasMore(names.length >= BRAND_LIMIT);
        } catch {
            if (!append) setBrandList([]);
            setBrandHasMore(false);
        } finally {
            setBrandLoading(false);
        }
    }, [uid, brandLoading]);

    // Initial brand load + reset on search change
    useEffect(() => {
        setBrandPage(1);
        setBrandHasMore(true);
        fetchBrands(1, brandSearch, false);
    }, [brandSearch]);

    // Close brand dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (brandRef.current && !brandRef.current.contains(e.target)) {
                setShowBrandDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Infinite scroll on brand dropdown
    const handleBrandScroll = useCallback((e) => {
        const el = e.target;
        // Scrolling up in an upward dropdown: content at top = near "bottom" of list
        if (el.scrollTop <= 20 && brandHasMore && !brandLoading) {
            const nextPage = brandPage + 1;
            setBrandPage(nextPage);
            fetchBrands(nextPage, brandSearch, true);
        }
    }, [brandPage, brandHasMore, brandLoading, brandSearch, fetchBrands]);

    // Flatten tree
    const flattenTree = (nodes, depth = 0) => {
        const result = [];
        for (const node of nodes) {
            result.push({ ...node, _depth: depth });
            if (node.children && node.children.length > 0) {
                result.push(...flattenTree(node.children, depth + 1));
            }
        }
        return result;
    };
    const flatOptions = flattenTree(categoryTree);

    const toggleCategory = useCallback((id) => {
        setSelectedCategories((prev) => {
            const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
            if (next.length > 0) {
                setCategoryData(next[0]);
                setSubCategoryId('');
                setSubCategoryData([]);
            } else {
                setCategoryData('');
            }
            if (next.length > 0 && errorFields.categorydata) {
                setErrorFields((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.categorydata;
                    return newErrors;
                });
            }
            return next;
        });
    }, [setCategoryData, setSubCategoryId, setSubCategoryData, errorFields.categorydata, setErrorFields, setSelectedCategories]);

    // Brand suggestions — filter out already-added brands
    const brandSuggestions = brandList.filter((b) => !brandNames.includes(b));

    const handleBrandInput = (e) => {
        const value = e.target.value;
        setBrandInput(value);
        setBrandSearch(value);
        setShowBrandDropdown(true);
    };

    const addBrandName = (name) => {
        const trimmed = name.trim();
        if (trimmed && !brandNames.includes(trimmed)) {
            setBrandNames([...brandNames, trimmed]);
        }
        setBrandInput('');
        setBrandSearch('');
        setShowBrandDropdown(false);
    };

    const addBrandOnEnter = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addBrandName(brandInput);
        }
    };

    const removeBrandName = (name) => {
        setBrandNames(brandNames.filter((b) => b !== name));
    };

    return (
        <div className={styles['AddProduct-card']}>
            <h2 className={styles['AddProduct-card-title']}>Category</h2>

            {/* Category tree selector */}
            <div className={styles['AddProduct-form-group']}>
                <label className={styles['AddProduct-label']}>
                    Categories{' '}
                    <span className={styles['AddProduct-required']}>*</span>
                </label>

                <div
                    style={{
                        border: errorFields.categorydata
                            ? '1px solid #e74c3c'
                            : '1px solid #ccc',
                        borderRadius: '6px',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        padding: '6px',
                        background: '#fafafa',
                    }}
                >
                    {flatOptions.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
                            No categories found
                        </div>
                    ) : (
                        flatOptions.map((opt) => {
                            const id = opt._id?.toString();
                            const isSelected = selectedCategories.includes(id);
                            const isPrimary = selectedCategories.length > 0 && selectedCategories[0] === id;
                            return (
                                <label
                                    key={id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '5px 6px',
                                        paddingLeft: `${(opt._depth || 0) * 16 + 6}px`,
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        borderRadius: '4px',
                                        background: isSelected ? '#e8f5e9' : 'transparent',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleCategory(id)}
                                        style={{ margin: 0 }}
                                    />
                                    <span style={{ flex: 1 }}>{opt.name}</span>
                                    <span style={{ color: '#aaa', fontSize: '11px', marginRight: '4px' }}>
                                        L{opt.level || 1}
                                    </span>
                                    {isPrimary && (
                                        <span style={{
                                            background: '#008060',
                                            color: '#fff',
                                            fontSize: '10px',
                                            padding: '1px 6px',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                        }}>
                                            Primary
                                        </span>
                                    )}
                                </label>
                            );
                        })
                    )}
                </div>
                {selectedCategories.length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
                        {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
                    </div>
                )}
                {errorFields.categorydata && (
                    <div className={styles['error-text']}>At least one category is required</div>
                )}
            </div>

            {/* Brand names with paginated dropdown */}
            <div className={styles['AddProduct-form-group']} ref={brandRef} style={{ position: 'relative' }}>
                <label className={styles['AddProduct-label']}>
                    Brand Names
                    <span className={styles['AddProduct-label-optional']}> (Optional)</span>
                </label>
                <input
                    type="text"
                    value={brandInput}
                    onChange={handleBrandInput}
                    onKeyDown={addBrandOnEnter}
                    onFocus={() => setShowBrandDropdown(true)}
                    placeholder="Search or type brand name"
                    className={styles['AddProduct-category']}
                    autoComplete="off"
                />

                {/* Brand dropdown — opens upward with scroll pagination */}
                {showBrandDropdown && (brandSuggestions.length > 0 || brandLoading) && (
                    <div
                        ref={brandDropdownRef}
                        onScroll={handleBrandScroll}
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            marginBottom: '4px',
                            background: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 10,
                            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column-reverse',
                        }}
                    >
                        <div>
                            {brandSuggestions.map((name) => (
                                <div
                                    key={name}
                                    onClick={() => addBrandName(name)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        borderBottom: '1px solid #f3f4f6',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                                >
                                    {name}
                                </div>
                            ))}
                            {brandLoading && (
                                <div style={{ padding: '8px 12px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                                    Loading...
                                </div>
                            )}
                            {!brandLoading && brandHasMore && brandSuggestions.length > 0 && (
                                <div style={{ padding: '6px 12px', textAlign: 'center', color: '#aaa', fontSize: '11px' }}>
                                    Scroll for more
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {brandNames.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                        {brandNames.map((name) => (
                            <span
                                key={name}
                                style={{
                                    background: '#e8f5e9',
                                    border: '1px solid #a5d6a7',
                                    borderRadius: '12px',
                                    padding: '3px 10px',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                {name}
                                <button
                                    type="button"
                                    onClick={() => removeBrandName(name)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#888',
                                        fontSize: '14px',
                                        lineHeight: 1,
                                        padding: 0,
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default CategorySection;
